import React, { useState, useCallback, useEffect } from 'react'
import { Stack, Typography, SvgIcon } from '@mui/material'
import { ArrowBackIos } from '@mui/icons-material'
import { ReactComponent as HeadsetIcon } from '../../../../../assets/images/headset.svg'
import { COLORS } from '../../../../../style/colors'
import { Toggle } from '../../../../../shared/components/Toggle/Toggle'
import { UnifiedDonutCharts, DonutChartData } from '../../../../../shared/components/DonutCharts'
import { EmptyPlaceholder } from '../../../../../shared/components/EmptyPlaceholder'
import Tabs from '../../../../../shared/components/Tabs/Tabs'
import TppCellDetailList from './TppCellDetailList'
import useTppElementSupport from '../useTppElementSupport'
import useTppDisplayName from '../../../../../shared/hooks/useDisplayNames/variants/useTppDisplayName'
import { TppCellMetric } from '../../../../../shared/queries/metrics/useGetTppElementSupportQuery'

interface TppColumnChartsProps {
    columnId: string
    highlightedOnly: boolean
    sectionIds?: string[]
    drillDownSectionId: string | null
    onDrillDownChange: (sectionId: string | null) => void
}

const TppColumnCharts = ({
    columnId,
    highlightedOnly,
    sectionIds,
    drillDownSectionId,
    onDrillDownChange,
}: TppColumnChartsProps) => {
    const [drillDownSectionTitle, setDrillDownSectionTitle] = useState<string>('')
    const [listData, setListData] = useState<DonutChartData<TppCellMetric> | null>(null)

    const { data, legend, isLoading, sectionIdMap } = useTppElementSupport(
        columnId,
        highlightedOnly,
        sectionIds,
        drillDownSectionId
    )

    const isEmpty = !data.length

    const handleTotalClick = useCallback(
        (chartData: DonutChartData<TppCellMetric>) => {
            const sectionId = sectionIdMap[chartData.title]
            if (sectionId) {
                onDrillDownChange(sectionId)
                setDrillDownSectionTitle(chartData.title)
            }
        },
        [sectionIdMap, onDrillDownChange]
    )

    const handleDrillDownTotalClick = useCallback((chartData: DonutChartData<TppCellMetric>) => {
        setListData(chartData)
    }, [])

    const handleBackClick = useCallback(() => {
        onDrillDownChange(null)
        setDrillDownSectionTitle('')
    }, [onDrillDownChange])

    // Reset local drill-down state when column changes
    useEffect(() => {
        setDrillDownSectionTitle('')
        setListData(null)
    }, [columnId])

    return (
        <>
            {isLoading && (
                <Stack
                    flex={1}
                    direction="column"
                    gap={1}
                    px={2}
                    py={2.75}
                    bgcolor="figma.grayscale.tier7"
                    borderRadius="16px"
                >
                    <Typography variant="14x400" color="figma.grayscale.textHier2">
                        Loading...
                    </Typography>
                </Stack>
            )}

            {!isLoading && isEmpty && (
                <Stack
                    flex={1}
                    direction="column"
                    gap={1}
                    px={2}
                    py={2.75}
                    bgcolor="figma.grayscale.tier7"
                    borderRadius="16px"
                >
                    <EmptyPlaceholder title="" subtitle="No data to display" />
                </Stack>
            )}

            {!isLoading && !isEmpty && (
                <Stack direction="column" gap={1}>
                    {drillDownSectionId && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            gap={0.5}
                            onClick={handleBackClick}
                            sx={{ cursor: 'pointer', '&:hover': { opacity: 0.7 } }}
                        >
                            <ArrowBackIos sx={{ fontSize: '12px', color: 'figma.primary' }} />
                            <Typography variant="14x400" color="figma.primary">
                                Back to All
                            </Typography>
                        </Stack>
                    )}
                    <UnifiedDonutCharts
                        key={drillDownSectionId ?? 'sections'}
                        data={data}
                        legend={legend}
                        firstChartSize={240}
                        onTotalClick={handleDrillDownTotalClick}
                        onTitleClick={drillDownSectionId ? handleDrillDownTotalClick : handleTotalClick}
                        firstChartOnTitleClick={handleDrillDownTotalClick}
                    />
                </Stack>
            )}

            {listData && (
                <TppCellDetailList
                    cells={listData.values.flatMap((v) => v.items)}
                    title={listData.title}
                    total={listData.values.flatMap((v) => v.items).length}
                    handleClose={() => setListData(null)}
                />
            )}
        </>
    )
}

interface TppElementSupportSectionProps {
    columns: { _id: string; title: string }[]
    selectedColumnId: string | null
    onColumnChange: (columnId: string) => void
    sectionIds?: string[]
    drillDownSectionId: string | null
    onDrillDownChange: (sectionId: string | null) => void
    highlightedOnly: boolean
    onHighlightedOnlyChange: (v: boolean) => void
}

const TppElementSupportSection = ({
    columns,
    selectedColumnId,
    onColumnChange,
    sectionIds,
    drillDownSectionId,
    onDrillDownChange,
    highlightedOnly,
    onHighlightedOnlyChange,
}: TppElementSupportSectionProps) => {
    const tppDisplayName = useTppDisplayName()

    if (!columns.length) return null

    const title = `${tppDisplayName.singular} Element Support`

    const handleToggle = () => {
        onHighlightedOnlyChange(!highlightedOnly)
    }

    const tabs = columns.map((col) => ({
        id: col._id,
        label: col.title,
        view: (
            <TppColumnCharts
                columnId={col._id}
                highlightedOnly={highlightedOnly}
                sectionIds={sectionIds}
                drillDownSectionId={drillDownSectionId}
                onDrillDownChange={onDrillDownChange}
            />
        ),
    }))

    return (
        <Stack direction="column" mb={3}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                <Stack direction="row" alignItems="center" gap={1} color="figma.grayscale.textHier1">
                    <SvgIcon
                        component={HeadsetIcon}
                        inheritViewBox
                        fontSize="small"
                        color="inherit"
                        sx={{ fontSize: '24px' }}
                    />
                    <Typography variant="20x500" color="inherit">
                        {title}
                    </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" gap={0.75}>
                    <Typography
                        variant="14x400"
                        onClick={() => onHighlightedOnlyChange(false)}
                        sx={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            color: !highlightedOnly ? 'figma.grayscale.textHier1' : 'figma.grayscale.textHier2',
                            '&:hover': { opacity: 0.8 },
                        }}
                    >
                        Show all
                    </Typography>
                    <Toggle checked={highlightedOnly} onChange={handleToggle} />
                    <Typography
                        variant="14x400"
                        onClick={() => onHighlightedOnlyChange(true)}
                        sx={{
                            cursor: 'pointer',
                            userSelect: 'none',
                            color: highlightedOnly ? 'figma.grayscale.textHier1' : 'figma.grayscale.textHier2',
                            '&:hover': { opacity: 0.8 },
                        }}
                    >
                        Executive Summary
                    </Typography>
                </Stack>
            </Stack>

            <Tabs
                orientation="horizontal"
                tabs={tabs}
                selectedTabId={selectedColumnId ?? columns[0]._id}
                onTabChange={onColumnChange}
                tabPanelSx={{ pt: 1, px: 0 }}
            />
        </Stack>
    )
}

export default TppElementSupportSection
